import * as React from "react";

import { PublicPayee } from "api/billingPublic";
import { CopyButton } from "Components/ui";

const Row: React.FC<{ label: string; value: string; copy?: boolean }> = ({
  label,
  value,
  copy = false,
}) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="flex min-w-0 items-center gap-1 font-mono text-sm text-foreground">
      <span className="truncate" title={value}>
        {value}
      </span>
      {copy && <CopyButton value={value} label={`Copy ${label.toLowerCase()}`} size="sm" />}
    </dd>
  </div>
);

/**
 * "Can't pay online?" — bank transfer / UPI details configured in SAP, and where to send the
 * transfer reference. Hidden when none are configured.
 */
const OtherWaysToPay: React.FC<{ payee: PublicPayee; amountLabel?: string }> = ({
  payee,
  amountLabel,
}) => {
  const { bankTransfer, upiId, supportEmail } = payee;
  if (!bankTransfer && !upiId) {
    return null;
  }
  return (
    <details className="rounded-card border border-border px-4 py-3 text-sm">
      <summary className="cursor-pointer font-medium text-foreground">
        Can't pay online? Pay by bank transfer{upiId ? " or UPI" : ""}
      </summary>
      <dl className="mt-2 divide-y divide-border">
        {bankTransfer && (
          <>
            {bankTransfer.accountName && (
              <Row label="Account name" value={bankTransfer.accountName} />
            )}
            <Row label="Account number" value={bankTransfer.accountNumber} copy />
            <Row label="IFSC" value={bankTransfer.ifsc} copy />
            {bankTransfer.bankName && <Row label="Bank" value={bankTransfer.bankName} />}
          </>
        )}
        {upiId && <Row label="UPI ID" value={upiId} copy />}
      </dl>
      <p className="mt-2 text-xs text-muted-foreground">
        Transfer {amountLabel || "the amount due"}
        {supportEmail ? (
          <>
            , then email the transaction reference (UTR) to{" "}
            <a className="font-medium text-primary-text underline" href={`mailto:${supportEmail}`}>
              {supportEmail}
            </a>
          </>
        ) : (
          ", then share the transaction reference (UTR) with Meta16 Labs"
        )}
        . It is applied once we receive it.
      </p>
    </details>
  );
};

export default OtherWaysToPay;
